import { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { ProcurementPurchaseRequestApprovableFragmentDoc } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'

import ContactInfo from './ContactInfo'

type ProcurementPurchaseRequestTaskContentProps = {
  id: number
  requesterContent: EmployeeTableViewRow
}

const ProcurementPurchaseRequestTaskContent: FC<ProcurementPurchaseRequestTaskContentProps> = ({
  id,
  requesterContent,
}) => {
  const {
    access: { locale: accessLocale },
  } = useCurrentSession()

  const { data } = useFragment({
    fragment: ProcurementPurchaseRequestApprovableFragmentDoc,
    fragmentName: 'ProcurementPurchaseRequestApprovable',
    from: {
      __typename: 'ProcurementPurchaseRequest',
      id,
    },
    optimistic: false,
  })

  if (!data) return null

  const costText = data.cost
    ? MoneyWithCurrency({
        amount: Number(data.cost.cents),
        currency: data.cost.currency,
        locale: accessLocale,
      })
    : undefined

  const purchaseRequestType = data.purchaseRequestType?.name

  const purchaseRequestUrl = data.url

  const summary = data.purchaseRequestDescription

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent,
    costText && {
      title: i18n.t('procurement.purchase_requests.detail.info.cost'),
      content: { text: costText, type: 'item' as const },
    },
    purchaseRequestType && {
      title: i18n.t('procurement.purchase_requests.detail.info.type'),
      content: { text: purchaseRequestType, type: 'item' as const },
    },
    purchaseRequestUrl && {
      title: i18n.t('procurement.purchase_requests.detail.info.url'),
      content: { text: purchaseRequestUrl, type: 'item' as const },
    },
    summary && {
      title: i18n.t('procurement.purchase_requests.detail.info.description'),
      content: { text: summary, type: 'item' as const },
    },
  ].filter(Boolean)

  return (
    <>
      <DetailsItemsList details={details} tableView />
      {!!data.additionalInformation && (
        <NoteField
          label={i18n.t('procurement.purchase_requests.detail.info.description')}
          content={data.additionalInformation}
        />
      )}
      {data.vendor?.id && <ContactInfo id={data.vendor.id} />}
    </>
  )
}

export default ProcurementPurchaseRequestTaskContent

export * from './metadata'
